import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combine } from 'src/app/shared/utilities/route';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { Observable } from 'rxjs';
import { Expertise, SpecialtyGridItem } from 'src/app/shared/models/expertise.model';
import { Profession } from 'src/app/shared/models/profession.model';
import {
    ExpertiseLateral,
    ExpertiseStepDown,
    ProfessionType,
    Specialty
} from 'src/app/shared/models/lookup-profession.model';
import { FetchArgs, PagingToken } from '../models/core';
import { generateQuery } from '../utilities/grid-query-params';

@Injectable({
    providedIn: 'root'
})
export class ProfessionsAndExpertiseService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) private readonly settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    create(professionId: number, expertise: Expertise): Observable<Expertise> {
        const url = `${this.coreApiPath}/expertise?professionId=${professionId}`;
        return this.http.post<Expertise>(url, expertise);
    }

    update(professionId: number, expertise: Expertise): Observable<Expertise> {
        const url = `${this.coreApiPath}/expertise/${expertise.id}?professionId=${professionId}`;
        return this.http.put<Expertise>(url, expertise);
    }

    getProfessions(): Observable<Profession[]> {
        const url = `${this.coreApiPath}/candidate/professions`;
        return this.http.get<Profession[]>(url);
    }

    getSpecialties(): Observable<Specialty[]> {
        const url = `${this.coreApiPath}/candidate/specialties`;
        return this.http.get<Specialty[]>(url);
    }

    getProfessionTypes(): Observable<ProfessionType[]> {
        const url = `${this.coreApiPath}/candidate/profession-types`;
        return this.http.get<ProfessionType[]>(url);
    }

    getExpertiseForProfession(professionId: number): Observable<Expertise[]> {
        const url = `${this.coreApiPath}/candidate/professions/${professionId}/expertise`;
        return this.http.get<Expertise[]>(url);
    }

    getExpertiseForProfessions(professionIds: number[]): Observable<Expertise[]> {
        const url = `${this.coreApiPath}/lookup/professions/expertises`;
        return this.http.post<Expertise[]>(url, { ProfessionIds: professionIds });
    }

    getExpertiseLaterals(professionId: number, expertiseId: number): Observable<ExpertiseLateral[]> {
        const url = `${this.coreApiPath}/expertise/laterals?professionId=${professionId}&expertiseId=${expertiseId}`;
        return this.http.get<ExpertiseLateral[]>(url);
    }

    decommissionExpertise(
        professionId: number,
        oldExpertiseId: number,
        newProfessionId: number,
        newExpertiseId: number
    ): Observable<void> {
        const url = `${this.coreApiPath}/professions/${professionId}/expertise/update`;

        return this.http.put<void>(url, {
            newProfessionId,
            newExpertiseId,
            oldExpertiseId
        });
    }

    saveExpertiseLaterals(
        professionId: number,
        expertiseId: number,
        lateralExpertiseIds: number[]
    ): Observable<unknown> {
        const url = `${this.coreApiPath}/expertise/laterals?professionId=${professionId}&expertiseId=${expertiseId}`;
        return this.http.put<any>(url, lateralExpertiseIds);
    }

    getExpertiseStepDowns(professionId: number, expertiseId: number): Observable<ExpertiseStepDown[]> {
        const url = `${this.coreApiPath}/expertise/step-downs?professionId=${professionId}&expertiseId=${expertiseId}`;
        return this.http.get<ExpertiseStepDown[]>(url);
    }

    saveExpertiseStepDowns(
        professionId: number,
        expertiseId: number,
        stepDownExpertiseIds: number[]
    ): Observable<unknown> {
        const url = `${this.coreApiPath}/expertise/step-downs?professionId=${professionId}&expertiseId=${expertiseId}`;
        return this.http.put<any>(url, stepDownExpertiseIds);
    }

    getSpecialtyGridItems(args: FetchArgs): Observable<PagingToken<SpecialtyGridItem[]>> {
        const url = `${this.coreApiPath}/expertise/specialty-grid-items`;
        const params = generateQuery(args);

        return this.http.get<PagingToken<SpecialtyGridItem[]>>(url, { params });
    }
}
